/**
 * Reglas del contrato de flotas que el front necesita conocer (validar antes de
 * enviar, pintar desgloses). El mock las usa también, para que las dos partes
 * respondan igual hasta que exista el backend.
 */
import {
  DemandChannel,
  DocumentOwnerType,
  DocumentType,
  DocumentUrgency,
  ExpiryStatus,
  FleetMode,
  ShiftSlot,
  Weekday,
} from './drivers-fleet.enums';
import {
  DocumentTypeInfo,
  FleetDocument,
  GeoPoint,
  PassengerFeeRule,
  PassengerFeeSimulation,
  PassengerFeeSimulationRequest,
  ShiftAssignment,
  ShiftConflict,
} from './drivers-fleet.models';

/** Días antes de caducar en los que un documento pasa a «caduca pronto». */
export const EXPIRING_SOON_DAYS = 45;

/** Ventana de «Próximas caducidades» en Documentos (4 meses), más los ya caducados. */
export const UPCOMING_EXPIRATIONS_DAYS = 120;

/** Ficheros admitidos al subir documentos (igual que la app, DDEV-5). */
export const DOCUMENT_UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.pdf';
export const DOCUMENT_UPLOAD_MIME_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'application/pdf'];
export const DOCUMENT_UPLOAD_MAX_BYTES = 30 * 1024 * 1024;

/**
 * Tipos de documento del contrato. El backend manda el mismo catálogo
 * (`fleet-catalogs.document_types`); esta copia es la del mock y el respaldo
 * mientras no llega.
 */
export const DOCUMENT_TYPES: readonly DocumentTypeInfo[] = [
  { type: 'bank_ownership_certificate', ownerType: 'fleet', modes: ['taxi', 'vtc'], hasExpiry: false, requiresLicenseNumber: false },
  { type: 'route_sheet_authorization', ownerType: 'fleet', modes: ['vtc'], hasExpiry: false, requiresLicenseNumber: false },
  { type: 'circulation_permit', ownerType: 'vehicle', modes: ['taxi', 'vtc'], hasExpiry: false, requiresLicenseNumber: false },
  { type: 'technical_sheet_itv', ownerType: 'vehicle', modes: ['taxi', 'vtc'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'insurance_policy', ownerType: 'vehicle', modes: ['taxi', 'vtc'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'taxi_municipal_license', ownerType: 'vehicle', modes: ['taxi'], hasExpiry: true, requiresLicenseNumber: true },
  { type: 'vtc_transport_card', ownerType: 'vehicle', modes: ['vtc'], hasExpiry: true, requiresLicenseNumber: true },
  { type: 'identity', ownerType: 'driver', modes: ['taxi', 'vtc'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'driving_license', ownerType: 'driver', modes: ['taxi', 'vtc'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'taxi_driver_card', ownerType: 'driver', modes: ['taxi'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'vtc_driver_permit', ownerType: 'driver', modes: ['vtc'], hasExpiry: true, requiresLicenseNumber: false },
  { type: 'social_security_registration', ownerType: 'driver', modes: ['vtc'], hasExpiry: false, requiresLicenseNumber: false },
];

export function documentTypeInfo(
  type: DocumentType,
  catalog: readonly DocumentTypeInfo[] = DOCUMENT_TYPES,
): DocumentTypeInfo | undefined {
  return catalog.find((info) => info.type === type);
}

/** Tipos que se piden a un dueño según el modo de la flota, en el orden del catálogo. */
export function documentTypesFor(
  ownerType: DocumentOwnerType,
  mode: FleetMode,
  catalog: readonly DocumentTypeInfo[] = DOCUMENT_TYPES,
): DocumentType[] {
  return catalog
    .filter((info) => info.ownerType === ownerType && info.modes.includes(mode))
    .map((info) => info.type);
}

/** Canales cuya demanda genera WAIIS: solo estos pagan cuota al pasajero. */
export const FEE_CHANNELS: readonly DemandChannel[] = ['waiis_app'];

export function feeApplies(channel: DemandChannel): boolean {
  return FEE_CHANNELS.includes(channel);
}

/**
 * Cuota al pasajero de un viaje según la matriz (docs/flotas-drivers/api-contract.md
 * › Modelo económico): cliente propio de la emisora → 0; ciudad con el
 * interruptor apagado → 0; si no, la regla más específica (ciudad y modo >
 * ciudad > modo > general) para el tipo de servicio. La cuota se suma al
 * precio: conductor y flota cobran `fareCents` íntegro.
 */
export function resolvePassengerFee(
  rules: readonly Pick<PassengerFeeRule, 'id' | 'region' | 'mode' | 'serviceType' | 'amountCents'>[],
  regionsEnabled: Readonly<Record<string, boolean>>,
  input: Omit<PassengerFeeSimulationRequest, 'fareCents'> & { fareCents: number },
): PassengerFeeSimulation {
  const total = (feeCents: number) => input.fareCents + feeCents;
  if (!feeApplies(input.demandChannel)) {
    return { feeCents: 0, passengerTotalCents: total(0), ruleId: null, reason: 'channel_exempt' };
  }
  if (regionsEnabled[input.regionId] === false) {
    return { feeCents: 0, passengerTotalCents: total(0), ruleId: null, reason: 'region_disabled' };
  }
  const score = (rule: (typeof rules)[number]): number => {
    if (rule.serviceType !== input.serviceType) return -1;
    if (rule.region && rule.region.id !== input.regionId) return -1;
    if (rule.mode && rule.mode !== input.mode) return -1;
    return (rule.region ? 2 : 0) + (rule.mode ? 1 : 0);
  };
  const best = rules.reduce<(typeof rules)[number] | null>((top, rule) => {
    const s = score(rule);
    return s >= 0 && (!top || s > score(top)) ? rule : top;
  }, null);
  if (!best) return { feeCents: 0, passengerTotalCents: total(0), ruleId: null, reason: 'no_rule' };
  return { feeCents: best.amountCents, passengerTotalCents: total(best.amountCents), ruleId: best.id, reason: 'rule' };
}

/** Estado de caducidad de una fecha `YYYY-MM-DD` respecto a `today` (por defecto, hoy). */
export function expiryStatusFor(expiresAt: string | null, today: Date = new Date()): ExpiryStatus {
  if (!expiresAt) {
    return 'none';
  }
  const [year, month, day] = expiresAt.slice(0, 10).split('-').map(Number);
  const expiry = Date.UTC(year, month - 1, day);
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const daysLeft = Math.round((expiry - start) / 86_400_000);
  if (daysLeft < 0) {
    return 'expired';
  }
  return daysLeft <= EXPIRING_SOON_DAYS ? 'expiring_soon' : 'valid';
}

const URGENCY_ORDER: readonly DocumentUrgency[] = ['expired', 'rejected', 'expiring_soon', 'in_review', 'valid'];

/** Urgencia de un documento para ordenar la matriz (caducado primero). */
export function documentUrgency(doc: Pick<FleetDocument, 'status' | 'expiryStatus'>): DocumentUrgency {
  if (doc.status === 'approved' && doc.expiryStatus === 'expired') {
    return 'expired';
  }
  if (doc.status === 'rejected') {
    return 'rejected';
  }
  if (doc.status === 'approved' && doc.expiryStatus === 'expiring_soon') {
    return 'expiring_soon';
  }
  if (doc.status === 'in_review' || doc.status === 'pending') {
    return 'in_review';
  }
  return 'valid';
}

/** La más urgente de una lista (para la fila de un vehículo). */
export function mostUrgent(urgencies: DocumentUrgency[]): DocumentUrgency {
  return urgencies.reduce<DocumentUrgency>(
    (worst, current) => (URGENCY_ORDER.indexOf(current) < URGENCY_ORDER.indexOf(worst) ? current : worst),
    'valid',
  );
}

export function urgencyRank(urgency: DocumentUrgency): number {
  return URGENCY_ORDER.indexOf(urgency);
}

/**
 * Conflictos de un patrón de turnos (mismas reglas que el backend):
 * un conductor en dos coches en la misma franja, o dos conductores en la misma
 * franja de un coche.
 */
export function findShiftConflicts(assignments: readonly ShiftAssignment[]): ShiftConflict[] {
  const conflicts: ShiftConflict[] = [];
  const bySlot = new Map<string, ShiftAssignment[]>();
  for (const assignment of assignments) {
    const key = `${assignment.weekday}|${assignment.slot}`;
    bySlot.set(key, [...(bySlot.get(key) ?? []), assignment]);
  }

  bySlot.forEach((slotAssignments) => {
    const { weekday, slot } = slotAssignments[0];

    const vehiclesByDriver = new Map<string, Set<string>>();
    const driversByVehicle = new Map<string, Set<string>>();
    for (const a of slotAssignments) {
      vehiclesByDriver.set(a.membershipId, (vehiclesByDriver.get(a.membershipId) ?? new Set()).add(a.vehicleId));
      driversByVehicle.set(a.vehicleId, (driversByVehicle.get(a.vehicleId) ?? new Set()).add(a.membershipId));
    }

    vehiclesByDriver.forEach((vehicleIds, membershipId) => {
      if (vehicleIds.size > 1) {
        conflicts.push({ kind: 'driver_double_booked', weekday, slot, membershipId, vehicleIds: [...vehicleIds] });
      }
    });
    driversByVehicle.forEach((membershipIds, vehicleId) => {
      if (membershipIds.size > 1) {
        conflicts.push({ kind: 'slot_taken', weekday, slot, vehicleId, membershipIds: [...membershipIds] });
      }
    });
  });

  return conflicts;
}

/** Clave de una celda de la cuadrícula de turnos. */
export function shiftCellKey(vehicleId: string, weekday: Weekday, slot: ShiftSlot): string {
  return `${vehicleId}|${weekday}|${slot}`;
}

// ------------------------------------------------------------------ geometría (paradas y zonas)

/** Punto dentro de un polígono `[lat, lng][]` (ray casting; vale a escala de ciudad). */
export function pointInPolygon(point: GeoPoint, polygon: readonly GeoPoint[]): boolean {
  const [y, x] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i];
    const [yj, xj] = polygon[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function segmentsCross(a: GeoPoint, b: GeoPoint, c: GeoPoint, d: GeoPoint): boolean {
  const orient = (p: GeoPoint, q: GeoPoint, r: GeoPoint) => Math.sign((q[1] - p[1]) * (r[0] - p[0]) - (q[0] - p[0]) * (r[1] - p[1]));
  return orient(a, b, c) * orient(a, b, d) < 0 && orient(c, d, a) * orient(c, d, b) < 0;
}

/** Polígono válido: al menos 3 puntos y sin lados que se crucen. */
export function isValidPolygon(polygon: readonly GeoPoint[]): boolean {
  if (polygon.length < 3) return false;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const adjacent = j === i + 1 || (i === 0 && j === n - 1);
      if (!adjacent && segmentsCross(polygon[i], polygon[(i + 1) % n], polygon[j], polygon[(j + 1) % n])) return false;
    }
  }
  return true;
}

/** Centro aproximado (media de los vértices). */
export function polygonCenter(polygon: readonly GeoPoint[]): GeoPoint {
  const n = polygon.length || 1;
  return [polygon.reduce((s, p) => s + p[0], 0) / n, polygon.reduce((s, p) => s + p[1], 0) / n];
}
